import { SiteUrls } from '@tableau/shared-urls';
import * as _ from 'underscore';
import { AddActiveDirectoryUsersAction } from '../../userManagement/services/AddActiveDirectoryUsersAction';
import { AddExternalUsersAction } from '../../userManagement/services/AddExternalUsersAction';
import { AddLocalUserAction, AddLocalUserDialogType } from '../../userManagement/services/AddLocalUserAction';
import { IAuthenticationProviderSelectorModel } from '../directives/tbAuthenticationProviderSelector';
import { IActionContext } from '../helpers/ActionContext';
import { Module } from '../Module';
import { SiteUsers } from '../resources/SiteUsers';
import { DomainFamily } from '../ServerApi/types/IServerSettingsUnauthenticated';
import { ActiveDirectoryGroupAction } from './ActiveDirectoryGroupAction';
import { BulkImportUsersAction } from './BulkImportUsersAction';
import { LicensesService } from './LicensesService';
import { IModalInstance, IModalOptions, ModalService, ModalSize } from './ModalService';
import { ServerService } from './ServerService';
import {
  AuthenticationProviderType,
  IAuthenticationProvider,
  SiteAuthenticationSettingsService,
} from './SiteAuthenticationSettingsService';

// triggered by sub-dialogs when one or more users have been added to the site or server
export let UserAddedEvent = 'UserAddedEvent';

interface IDescriptionTranslationValues {
    SERVER_NAME: string;
    PROVIDER_NAME: string;
    SITE_INVITE_NOTIF_SETTINGS_URL?: string;
}

export class AddUsersOptionsAction {

  public static $inject = ['$translate', 'ModalService', 'ServerService', 'SiteAuthenticationSettingsService'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private modal: ModalService,
    private serverService: ServerService,
    private siteAuthenticationSettingsService: SiteAuthenticationSettingsService) {
  }

  public getInitialAuthenticationProviderSelectorModel(): IAuthenticationProviderSelectorModel {
    return {
      getProviderDisplayName: (provider: IAuthenticationProvider) => {
        const translateValues = {
          PROVIDER_NAME: this.siteAuthenticationSettingsService.getProviderDisplayName(provider),
        };
        return this.$translate.instant('addUsersWithAuthentication', translateValues);
      },
      getProviderDescription: (provider: IAuthenticationProvider, inviteNotificationEnabledForSite: boolean) => {
        const determineTranslateKey = (): string => {
          let translateKey = null;
          if (provider.type === AuthenticationProviderType.default) {
            if (this.serverService.isTableauIdSupported()) {
              translateKey = 'addUsersWithAuthentication_description_tableauid';
            }
          } else {
            // conditional descriptions for 1) both when the server config for site invite notifications is turned on and off and for 2) Site level settings on/off
            translateKey = this.serverService.isSiteInviteEmailEnabledForServer()
              ? (inviteNotificationEnabledForSite ? 'addUsersWithAuthentication_description_external_email' : 'addUsersWithAuthentication_description_external_email_off')
              : 'addUsersWithAuthentication_description_external';
          }
          return translateKey;
        };

        const buildTranslatonValues = (): IDescriptionTranslationValues => {
          const translateValues: IDescriptionTranslationValues = {
            SERVER_NAME: this.serverService.serverName(),
            PROVIDER_NAME: this.siteAuthenticationSettingsService.getProviderDisplayName(provider)
          };
          if (this.serverService.isSiteInviteEmailEnabledForServer() && !inviteNotificationEnabledForSite) {
            const siteUrls = new SiteUrls({
              siteUrlName: this.serverService.sessionSiteUrlName(),
              queryString: window.location.search
            });
            translateValues['SITE_INVITE_NOTIF_SETTINGS_URL'] = `<a href=${siteUrls.settingsUrl()}>${this.$translate.instant('goToSiteInviteNotifSettings')}</a>`;
          }
          return translateValues;
        };

        const translateKey = determineTranslateKey();
        return translateKey
            ? this.$translate.instant(translateKey, buildTranslatonValues())
            : '';
      },
      authenticationProvider: this.siteAuthenticationSettingsService.getDefaultProvider(),
      authSetting: 'DEFAULT', // should be removed by TFSID:607630
      authenticationProviderIds: undefined
    };
  }

  public showAddSiteUsersOptionsDialog(context: IActionContext): ng.IPromise<any> {
    const siteUserTestId = 'site-user-opions';
    const dialogInstance = this.createDialogInstance(
      context, AddSiteUsersOptionsDialogCtrl, siteUserTestId);

    return dialogInstance.result;
  }

  public showAddServerUsersOptionsDialog(context: IActionContext): ng.IPromise<any> {
    const serverUserTestId = 'server-user-opions';
    const dialogInstance = this.createDialogInstance(
      context, AddServerUsersOptionsDialogCtrl, serverUserTestId);

    return dialogInstance.result;
  }

  private createDialogInstance(context: IActionContext, controller: any, testId: string): IModalInstance {

    return this.modal.open({
      scope: context.scope,
      templateUrl: 'addUsersOptionsDialog.html',
      size: ModalSize.fitToContent,
      position: _.extend(ModalService.anchoredTo(context.targetElement)),
      controller: controller,
      resolve: {
        domainFamily: () => this.serverService.domainFamily()
      },
      tbTestId: testId
    });
  }

}

Module.instance().service('AddUsersOptionsAction', AddUsersOptionsAction);

// controller

export interface IAddUsersOptionsDialogScope extends ng.IScope {
  title: string;
  // functions tied to the various add buttons
  addLocalUser(): void;
  importAdUser(): void;
  importAdGroup?(): void;
  importFromBulk(): void;
  importExternalUser(): void;
  // from modal dialog
  closeAndResolveAfterPromise: (result: any, promise: ng.IPromise<any>) => void;
  options: IModalOptions;
}

export class AddSiteUsersOptionsDialogCtrl {

  private addLocalUserAction: AddLocalUserAction = AddLocalUserAction.instance;

  static $inject = [
    '$scope',
    'ActiveDirectoryGroupAction',
    'BulkImportUsersAction',
    'domainFamily',
    'SiteUsers',
    'LicensesService'];

  constructor(
    $scope: IAddUsersOptionsDialogScope,
    activeDirectoryGroupAction: ActiveDirectoryGroupAction,
    bulkImportUsersAction: BulkImportUsersAction,
    domainFamily: string,
    private siteUsers: SiteUsers,
    private licenseService: LicensesService) {
    $scope.title = 'addUsersToSite';

    if(domainFamily === DomainFamily.Local){
      $scope.addLocalUser = () => {
        this.getAvailableQuota().then((availableQuota: number) => {
          openChildModal($scope, () => {
            return this.addLocalUserAction.showAddLocalUserDialog(AddLocalUserDialogType.SITE, availableQuota);
          });
        });
      };
    }

    if(domainFamily === DomainFamily.ActiveDirectory){
      $scope.importAdUser = () => {
        this.getAvailableQuota().then((availableQuota: number) => {
          openChildModal($scope, () => {
            return AddActiveDirectoryUsersAction.showAddActiveDirectoryUsersToSiteDialog(availableQuota);
          });
        });
      };
      $scope.importAdGroup = () => {
        openChildModal($scope, () => {
          return activeDirectoryGroupAction.showAddAdGroupDialog($scope.$parent);
        });
      };
    }

    if(domainFamily === DomainFamily.External){
      $scope.importExternalUser = () => {
        this.getAvailableQuota().then((availableQuota: number) => {
          openChildModal($scope, () => {
            return AddExternalUsersAction.showAddExternalUsersToSiteDialog($scope.$parent, availableQuota);
          });
        });
      };
    }

    $scope.importFromBulk = () => {
      openChildModal($scope, () => {
        return bulkImportUsersAction.showBulkImportSiteUsersDialog($scope.$parent);
      });
    };
  }

  public getAvailableQuota() {
    return this.licenseService.hasSiteLicensingConsumptionInfo().then((hasCapacity: boolean) => {
      if (!hasCapacity) {
        return this.siteUsers.getRemainingUserQuota();
      } else {
        return undefined;
      }
    });
  }
}

function openChildModal(scope: IAddUsersOptionsDialogScope, childModalOpenAction: (...args: any[]) => ng.IPromise<any>) {
  // Note: The order here is important, because $scope.closeAndResolveAfterPromise() will destroy the scope.
  let promise = childModalOpenAction();
  scope.closeAndResolveAfterPromise({}, promise);
}

export class AddServerUsersOptionsDialogCtrl {

  private addLocalUserAction: AddLocalUserAction = AddLocalUserAction.instance;

  static $inject = [
    '$scope',
    'BulkImportUsersAction',
    'domainFamily'];

  constructor(
    $scope: IAddUsersOptionsDialogScope,
    bulkImportUsersAction: BulkImportUsersAction,
    domainFamily: string) {
    $scope.title = 'addUsersToServer';

    if (domainFamily === DomainFamily.Local) {
      $scope.addLocalUser = () => {
        openChildModal($scope, () => {
          return this.addLocalUserAction.showAddLocalUserDialog(AddLocalUserDialogType.SERVER);
        });
      };
    }

    if (domainFamily === DomainFamily.ActiveDirectory) {
      $scope.importAdUser = () => {
        openChildModal($scope, () => {
          return AddActiveDirectoryUsersAction.showAddActiveDirectoryUsersToServerDialog();
        });
      };
    }

    $scope.importFromBulk = () => {
      openChildModal($scope, () => {
        return bulkImportUsersAction.showBulkImportServerUsersDialog($scope.$parent);
      });
    };
  }

}

Module.instance().controller('AddSiteUsersOptionsDialogCtrl', AddSiteUsersOptionsDialogCtrl);
Module.instance().controller('AddServerUsersOptionsDialogCtrl', AddServerUsersOptionsDialogCtrl);
